import { Box, CardActionArea, LinearProgress, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import type { ReactNode } from 'react';
import type { InventoryActivityItem, InventorySummary } from '@app/shared';
import { ErrorState, LoadingState } from '../../components/PageState';
import { useInventoryActivity, useInventorySummary } from './api';

const CHART_HEIGHT = 260;
const formatCount = (n: number) => n.toLocaleString('en-US');

type TileTarget = 'sim' | 'device';
/** The list filter a tile sets: its status, or eSIM / physical. */
export interface TileFilter {
  status?: string;
  esim?: string;
}
type OpenTile = (target: TileTarget, filter?: TileFilter) => void;

/**
 * One KPI tile. Status tiles also show their share of the total, as the reference does. With
 * `onOpen`, the tile opens its tab of the list: an IMEI tile the Device / IMEI tab, a SIM tile
 * the SIM tab (as on the reference).
 */
function KpiCard({
  label,
  value,
  total,
  target,
  filter,
  onOpen,
}: {
  label: string;
  value: number;
  total?: number;
  target: TileTarget;
  filter?: TileFilter;
  onOpen?: OpenTile;
}) {
  const percent = total ? Math.round((value / total) * 1000) / 10 : 0;
  const body = (
    <Box sx={{ p: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h1" component="p">
        {formatCount(value)}
      </Typography>
      {total !== undefined ? (
        <Typography variant="caption" color="text.secondary">
          {percent}% of total
        </Typography>
      ) : null}
    </Box>
  );
  return (
    <Paper variant="outlined">
      {onOpen ? (
        <CardActionArea
          onClick={() => onOpen(target, filter)}
          aria-label={`${label}: ${formatCount(value)}. Open the ${target === 'sim' ? 'SIM Inventory' : 'Device / IMEI Inventory'} tab`}
        >
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Paper>
  );
}

function ChartCard({
  title,
  empty,
  children,
}: {
  title: string;
  empty: boolean;
  children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minWidth: 0 }} role="figure" aria-label={title}>
      <Typography variant="h3" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {empty ? (
        <Box
          sx={{
            height: CHART_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography color="text.secondary">No data to show yet.</Typography>
        </Box>
      ) : (
        children
      )}
    </Paper>
  );
}

function Kpis({ summary, onOpen }: { summary: InventorySummary; onOpen?: OpenTile }) {
  const { sim, device } = summary;
  const tile = (
    label: string,
    value: number,
    target: TileTarget,
    total?: number,
    filter?: TileFilter,
  ) => (
    <KpiCard
      key={label}
      label={label}
      value={value}
      total={total}
      target={target}
      filter={filter}
      onOpen={onOpen}
    />
  );
  // Order of the reference dashboard: IMEI then SIM for each status, then the SIM types.
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 1.5,
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)' },
        mb: 1.5,
      }}
    >
      {tile('Total IMEI', device.total, 'device')}
      {tile('Total SIM', sim.total, 'sim')}
      {tile('Available IMEI', device.available, 'device', device.total, { status: 'Free' })}
      {tile('Available SIM', sim.available, 'sim', sim.total, { status: 'Free' })}
      {tile('Used IMEI', device.used, 'device', device.total, { status: 'Used' })}
      {tile('Used SIM', sim.used, 'sim', sim.total, { status: 'Used' })}
      {tile('Bad IMEI', device.bad, 'device', device.total, { status: 'Bad' })}
      {tile('Bad SIM', sim.bad, 'sim', sim.total, { status: 'Bad' })}
      {tile('Removed IMEI', device.removed, 'device', device.total, { status: 'Removed' })}
      {tile('Removed SIM', sim.removed, 'sim', sim.total, { status: 'Removed' })}
      {tile('e-SIM', sim.esim, 'sim', undefined, { esim: 'true' })}
      {tile('Physical SIM', sim.physical, 'sim', undefined, { esim: 'false' })}
    </Box>
  );
}

function Charts({ summary }: { summary: InventorySummary }) {
  const { palette } = useTheme();
  const { sim, device, simTrend, simByCarrier, topProducts } = summary;

  // Assumption: the reference chart's content was not readable; this shows SIMs and devices
  // together, split by the four statuses of the KPI tiles.
  const byStatus = [
    {
      id: 'available',
      label: 'Available',
      value: sim.available + device.available,
      color: palette.success.main,
    },
    { id: 'used', label: 'Used', value: sim.used + device.used, color: palette.info.main },
    { id: 'bad', label: 'Bad', value: sim.bad + device.bad, color: palette.error.main },
    {
      id: 'removed',
      label: 'Removed',
      value: sim.removed + device.removed,
      color: palette.grey[500],
    },
  ];

  return (
    <Box
      sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, mb: 2 }}
    >
      <ChartCard title="Inventory Status" empty={byStatus.every((s) => s.value === 0)}>
        <PieChart
          height={CHART_HEIGHT}
          series={[
            {
              data: byStatus,
              innerRadius: 50,
              paddingAngle: 1,
              valueFormatter: (item) => formatCount(item.value),
            },
          ]}
        />
      </ChartCard>

      <ChartCard
        title="SIM Inventory Trend (Last 30 Days)"
        empty={simTrend.every((d) => d.count === 0)}
      >
        <LineChart
          height={CHART_HEIGHT}
          // "2026-01-31" is shown as "01/31".
          xAxis={[
            {
              scaleType: 'point',
              data: simTrend.map((d) => `${d.date.slice(5, 7)}/${d.date.slice(8, 10)}`),
            },
          ]}
          yAxis={[{ tickMinStep: 1, min: 0 }]}
          series={[
            {
              data: simTrend.map((d) => d.count),
              label: 'SIMs added',
              color: palette.primary.main,
              showMark: false,
            },
          ]}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>

      <ChartCard title="SIM Inventory by Carrier" empty={simByCarrier.length === 0}>
        <BarChart
          height={CHART_HEIGHT}
          xAxis={[{ scaleType: 'band', data: simByCarrier.map((c) => c.carrier) }]}
          yAxis={[{ tickMinStep: 1 }]}
          series={[
            { data: simByCarrier.map((c) => c.count), label: 'SIMs', color: palette.primary.main },
          ]}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>

      <ChartCard title="Top 5 Products by SIM Count" empty={topProducts.length === 0}>
        <BarChart
          height={CHART_HEIGHT}
          layout="horizontal"
          yAxis={[{ scaleType: 'band', data: topProducts.map((p) => p.product) }]}
          xAxis={[{ tickMinStep: 1 }]}
          series={[
            { data: topProducts.map((p) => p.count), label: 'SIMs', color: palette.info.main },
          ]}
          margin={{ left: 170 }}
          slotProps={{ legend: { hidden: true } }}
        />
      </ChartCard>
    </Box>
  );
}

/** "3 days ago", as the reference words the time of an activity line. */
function timeAgo(at: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - new Date(at).getTime()) / 1000));
  if (seconds < 60) return `${seconds} sec ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  const unit = (n: number, word: string) => `${n} ${word}${n > 1 ? 's' : ''} ago`;
  if (seconds < 86_400) return unit(Math.floor(seconds / 3600), 'hour');
  return unit(Math.floor(seconds / 86_400), 'day');
}

/**
 * "SIM & Device Add/Update Activity" and "Inventory Health" of the Full Inventory Dashboard
 * (walk of 2026-10-27, third round): the latest adds and updates, each with a coloured dot, and
 * the health figures.
 */
export function ActivityAndHealth() {
  const { palette } = useTheme();
  const full = useInventoryActivity();
  if (full.isPending) return <LoadingState label="Loading inventory activity…" />;
  if (full.isError) return <ErrorState error={full.error} onRetry={() => void full.refetch()} />;
  const { activity, health } = full.data;
  const dot: Record<InventoryActivityItem['type'], string> = {
    sim: palette.success.main,
    device: palette.primary.main,
    bad: palette.error.main,
    removed: palette.warning.main,
    update: palette.secondary.main,
  };
  const figures: Array<[label: string, value: string]> = [
    ['Inventory Accuracy', `${health.accuracyPercent}%`],
    ['Pending Actions', formatCount(health.pendingActions)],
    ["Today's Transactions", formatCount(health.todayTransactions)],
  ];
  return (
    <Box
      sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' }, mb: 2 }}
    >
      <Paper variant="outlined" sx={{ p: 1.5 }} component="section" aria-labelledby="inv-activity">
        <Typography id="inv-activity" variant="h3" sx={{ mb: 1 }}>
          SIM &amp; Device Add/Update Activity
        </Typography>
        {activity.length === 0 ? (
          <Typography color="text.secondary">No recent activity.</Typography>
        ) : (
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            {activity.map((line, i) => (
              <Box
                component="li"
                key={`${line.at}-${i}`}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}
              >
                <Box
                  aria-hidden
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot[line.type] }}
                />
                <Typography sx={{ flexGrow: 1, overflowWrap: 'anywhere' }}>{line.text}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {timeAgo(line.at)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.5 }} component="section" aria-labelledby="inv-health">
        <Typography id="inv-health" variant="h3" sx={{ mb: 1 }}>
          Inventory Health
        </Typography>
        {figures.map(([label, value]) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
            <Typography>{label}</Typography>
            <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
          </Box>
        ))}
        <LinearProgress
          variant="determinate"
          value={health.accuracyPercent}
          aria-label="Inventory Accuracy"
          sx={{ mt: 1 }}
        />
      </Paper>
    </Box>
  );
}

/** KPI tiles and charts of the Inventory Dashboard, with their own loading and error states. */
export function SummaryPanel({ onOpen }: { onOpen?: OpenTile } = {}) {
  const summary = useInventorySummary();
  if (summary.isPending) return <LoadingState label="Loading inventory summary…" />;
  if (summary.isError)
    return <ErrorState error={summary.error} onRetry={() => void summary.refetch()} />;
  return (
    <Box aria-label="Inventory summary" component="section">
      <Kpis summary={summary.data} onOpen={onOpen} />
      <Charts summary={summary.data} />
    </Box>
  );
}
